// ════════════════════════════════════════════════════════════════════════
//  „Otto tippt los“ – Lehrgang, Fassung 1
//
//  Quelle ist ab jetzt das Lehrbuch von Simone Müller, 1. Auflage August 2026,
//  Kapitel 4 „Grundreihe“ (Seiten 12–22) und Kapitel 5 „Großschreibung“
//  (Seiten 24–26). Eine Lektion je Buchabschnitt: 4a bis 4h, dann 5a und 5b.
//  Die „Meilensteine“ des Hefts sind keine Lektionen – die hakt die Lehrkraft
//  auf Papier ab. Der Abschnitt steht im Untertitel („Heft 4c · …“) und nicht im
//  Titel: die Lektionsnummer 1–10 zeigt die App ohnehin schon als Plakette an,
//  „1  4c Mittelfinger“ nebeneinander wäre für ein Kind zwei Nummern für
//  dieselbe Sache. Der frühere Zuschnitt stammte aus dem Einzeldatei-Prototyp
//  (docs/referenz/) und passte nicht zur Buchgliederung – Kind und Lehrkraft
//  sollen Heftseite und Bildschirm nebeneinanderlegen können.
//
//  Die Übungszeilen sind ZEICHENGENAU aus dem Heft übernommen. Das „_“ des
//  Hefts markiert dort die Leertaste und steht hier als echtes Leerzeichen.
//  Änderungen nur nach Rücksprache mit der Lehrkraft.
//
//  Dieses Bundle hat zwei Aufgaben:
//    1. eingebackene Fallback-Fassung der Lern-App (Gastmodus ohne Serverkontakt)
//    2. Seed für curriculum_versions Version 1
//
//  Die Schlüssel sind ab dem ersten Veröffentlichen UNVERÄNDERLICH – an ihnen
//  hängen die Fortschrittszeilen der Kinder. Noch ist nichts ausgeliefert,
//  deshalb ist der Neuzuschnitt jetzt möglich und später nicht mehr.
// ════════════════════════════════════════════════════════════════════════

import type { Bundle } from "./schema.js";

export const OTTO_BUNDLE: Bundle = {
  schemaVersion: 1,
  slug: "grundkurs-1",
  title: "Grundkurs 1",
  starThresholds: { three: 0.95, two: 0.85 },

  // Das Tastaturbild ist die deutsche Tastatur, nicht nur die gelernten Tasten:
  // ein Kind soll auf dem Bildschirm dieselbe Anordnung sehen wie unter den
  // Fingern. Deshalb steht das "<" links in der unteren Reihe – dort sitzt es
  // auf jeder deutschen Tastatur, zwischen der linken Umschalttaste und dem y.
  keyboard: {
    // Die ganze deutsche Tastatur (T1), nicht nur die gelernten Reihen: ein
    // Kind soll auf dem Bildschirm dieselbe Anordnung sehen wie unter den
    // Fingern. Ungelernte Tasten sind grau.
    //
    // Vollständig schon bei der Auslieferung, damit Kapitel 6–18 (Satzzeichen,
    // Ziffern, ß) nur noch Lektionen brauchen und keine Tastaturarbeit.
    //
    // Die Zusatztasten – Umschalt, Feststell, Tabulator, Eingabe – stehen nicht
    // hier: sie sind keine Zeichen, und das Schema verlangt genau eines je
    // Taste. Die Lern-App zeichnet sie an den benannten Reihen.
    rows: [
      {
        id: "zahlen",
        keys: ["^", "1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "ß", "´"],
      },
      { id: "oben", keys: ["q", "w", "e", "r", "t", "z", "u", "i", "o", "p", "ü", "+"] },
      { id: "grund", keys: ["a", "s", "d", "f", "g", "h", "j", "k", "l", "ö", "ä", "#"] },
      { id: "unten", keys: ["<", "y", "x", "c", "v", "b", "n", "m", ",", ".", "-"] },
      { id: "leer", keys: [" "] },
    ],
    homeRow: [
      { key: "a", color: "#9B7EDE" },
      { key: "s", color: "#4E9AF1" },
      { key: "d", color: "#2FBF8F" },
      { key: "f", color: "#F5B83D" },
      { key: "j", color: "#F58B4C" },
      { key: "k", color: "#2FBF8F" },
      { key: "l", color: "#4E9AF1" },
      { key: "ö", color: "#9B7EDE" },
    ],
  },

  // Nur die Tasten, die Kapitel 4 und 5 behandeln. g, h und ä sind
  // Ausweichbewegungen aus der Grundstellung heraus – sie tragen deshalb die
  // Farbe des Fingers, der sie holt. e und i sind hier absichtlich nicht dabei:
  // die Oberreihe ist Kapitel 7, und eine eingefärbte Taste behauptet, sie sei
  // schon dran.
  //
  // Die Großbuchstaben stehen als eigene Einträge darunter – derselbe Finger,
  // derselbe Name. WELCHE Zusatztaste dazukommt, sagt nicht der Finger, sondern
  // die Lektion (`uppercaseWith`): 5a die Umschalttaste, 5b die Feststelltaste.
  // WELCHE SEITE, steckt weiter hier – im „linker …“/„rechter …“ des Fingers,
  // der den kleinen Buchstaben holt. Das ist die MERKE-Regel von Seite 24.
  // Neun Einträge für zehn Finger – die Daumen teilen sich einen, für die
  // Leertaste spielt die Seite keine Rolle. Vorher stand hier je TASTE eine
  // Zeile: 23 für zwölf Namen, mit der Farbe jedes Mal abgeschrieben.
  //
  // Die Farben stammen aus dem erprobten Prototyp und sind unverändert. Der
  // Zusatz „(nach rechts)“ ist entfallen: er benennt keinen Finger, sondern eine
  // Bewegung, und die gehört in den Hinweis der Stufe.
  //
  // Zugeordnet ist die GANZE Tastatur, nicht nur die Grundreihe – nach dem
  // Zehnfingersystem. Ohne das stünden vierzig graue Tasten ohne Farbe da, und
  // die erste Ziffernlektion begänne mit Tabellenarbeit statt mit Inhalt.
  //
  // Großbuchstaben stehen nur dort, wo der Lehrgang sie schon braucht (Kapitel
  // 5, Grundreihe). Auf Vorrat angelegt wären sie eine Behauptung über Tasten,
  // die noch keine Lektion anfasst.
  fingers: [
    {
      id: "links-klein",
      name: "linker kleiner Finger",
      color: "#9B7EDE",
      keys: ["^", "1", "q", "a", "A", "y", "<"],
    },
    {
      id: "links-ring",
      name: "linker Ringfinger",
      color: "#4E9AF1",
      keys: ["2", "w", "s", "S", "x"],
    },
    {
      id: "links-mittel",
      name: "linker Mittelfinger",
      color: "#2FBF8F",
      keys: ["3", "e", "d", "D", "c"],
    },
    {
      id: "links-zeige",
      name: "linker Zeigefinger",
      color: "#F5B83D",
      keys: ["4", "5", "r", "t", "f", "F", "g", "G", "v", "b"],
    },
    { id: "daumen", name: "Daumen", color: "#8CA3B5", keys: [" "] },
    {
      id: "rechts-zeige",
      name: "rechter Zeigefinger",
      color: "#F58B4C",
      keys: ["6", "7", "z", "u", "h", "H", "j", "J", "n", "m"],
    },
    {
      id: "rechts-mittel",
      name: "rechter Mittelfinger",
      color: "#2FBF8F",
      keys: ["8", "i", "k", "K", ","],
    },
    {
      id: "rechts-ring",
      name: "rechter Ringfinger",
      color: "#4E9AF1",
      keys: ["9", "o", "l", "L", "."],
    },
    {
      id: "rechts-klein",
      name: "rechter kleiner Finger",
      color: "#9B7EDE",
      keys: ["0", "ß", "´", "p", "ü", "+", "ö", "Ö", "ä", "Ä", "#", "-"],
    },
  ],

  lessons: [
    // ── 4a ───────────────────────────────────────────────────────────── S. 12
    {
      key: "l-fx25watu",
      number: 1,
      order: 0,
      title: "Zeigefinger F und J",
      subtitle: "Heft 4a · Die zwei Tasten mit der Erhebung",
      color: "#F5B83D",
      keys: ["f", "j", " "],
      intro:
        "Wir beginnen mit der Grundreihe. Die Tasten f und j sind etwas Besonderes: dort liegen deine Zeigefinger. Spürst du die kleinen Erhebungen? An ihnen findest du immer zurück, ohne hinzusehen.",
      stages: [
        {
          key: "s-qi0754if",
          title: "f und j einzeln",
          hint: "Linker Zeigefinger auf f, rechter Zeigefinger auf j. Erst eine Hand, dann die andere.",
          text: "ff ff ff ff ff jj jj jj jj jj",
        },
        {
          key: "s-cz7wo4ta",
          title: "f und j im Wechsel",
          hint: "Jede Lücke ist ein Druck auf die Leertaste – dafür ist der Daumen da.",
          text: "ff jj ff jj ff jj ff jj ff jj fff jjj fff jjj fff jjj fff jjj",
        },
        {
          key: "s-bf0s255y",
          title: "f und j gemischt",
          hint: "Jetzt stehen sie dicht beieinander. Langsam und genau – das Tempo kommt später.",
          text: "fj fj fj ffj ffj ffj fjf fjf",
        },
      ],
    },

    // ── 4b ───────────────────────────────────────────────────────────── S. 13
    {
      key: "l-vnbyx1co",
      number: 2,
      order: 1,
      title: "Zeigefinger G und H",
      subtitle: "Heft 4b · Die Zeigefinger wandern weiter",
      color: "#F58B4C",
      keys: ["f", "g", "h", "j", " "],
      intro:
        "Lege deine Finger zuerst in die Grundstellung. Jetzt bewegen sich nur die Zeigefinger: der linke nach rechts auf das g, der rechte nach links auf das h. Danach gehen sie sofort zurück.",
      stages: [
        {
          key: "s-qchmoyfj",
          title: "g und h einzeln",
          hint: "Nur der Zeigefinger bewegt sich. Die anderen Finger bleiben liegen.",
          text: "gg gg gg gg gg hh hh hh hh hh",
        },
        {
          key: "s-zwcxepag",
          title: "Hin und zurück",
          hint: "Nach jedem g und h findet der Finger wieder seine Taste f oder j.",
          text: "g h g g h g h h g fg fg fg fg jh jh jh",
        },
        {
          key: "s-o6zb4pi4",
          title: "Vier Tasten",
          hint: "Schaffst du die letzte Zeile in einem Rutsch: f – g – h – j?",
          text: "ghg ghg ghg hgh hgh fgf fgf fgf jhj jhj jhj ffj ggj fghj fghj fghj fghj",
        },
      ],
    },

    // ── 4c ───────────────────────────────────────────────────────────── S. 14
    {
      key: "l-asqkn7nc",
      number: 3,
      order: 2,
      title: "Mittelfinger D und K",
      subtitle: "Heft 4c · Die Mittelfinger sind dran",
      color: "#2FBF8F",
      keys: ["d", "f", "g", "h", "j", "k", " "],
      intro:
        "Der linke Mittelfinger tippt d, der rechte Mittelfinger tippt k. Beide liegen schon richtig – du musst sie nur drücken.",
      stages: [
        {
          key: "s-xr82aqt5",
          title: "d und k einzeln",
          hint: "Drei gleiche Buchstaben nacheinander: derselbe Finger, dreimal kurz.",
          text: "ddd ddd ddd ddd ddd kkk kkk kkk kkk kkk",
        },
        {
          key: "s-jw5r606a",
          title: "d und k mit den Zeigefingern",
          hint: "Zeigefinger, Mittelfinger, Zeigefinger – immer im gleichen Takt.",
          text: "fdf jkj fdf jkj fdf jkj fdf jkj fdfd jkjk fdfd jkjk fdfd jkjk",
        },
        {
          key: "s-ok1w51uh",
          title: "Über die Hand hinweg",
          hint: "Jetzt springt es zwischen links und rechts. Schau nicht auf die Tasten.",
          text: "fgd fgd fgd fgd fgd fgd jhk jhk jhk jhk jhk jhk",
        },
        {
          key: "s-zdxqc3rs",
          title: "Gemischt",
          hint: "Fünf Tasten, sechs Finger – lies jede Gruppe erst, dann tippe sie.",
          text: "hfk hfk hfk hfk hfk khd khd khd khd khd jgd jgd jgd jgd jgd",
        },
      ],
    },

    // ── 4d ───────────────────────────────────────────────────────────── S. 15
    {
      key: "l-h9py95ir",
      number: 4,
      order: 3,
      title: "D F G H J K üben",
      subtitle: "Heft 4d · Es wird anspruchsvoller",
      color: "#2FBF8F",
      keys: ["d", "f", "g", "h", "j", "k", " "],
      intro:
        "Sechs Tasten sind jetzt zusammengekommen. In diesen Gruppen steckt kein Wort und keine Regel – schau also genau hin, bevor du tippst.",
      stages: [
        {
          key: "s-puxq0bg8",
          title: "Vierergruppen",
          hint: "Immer vier Buchstaben, dann eine Lücke. Lies die Gruppe zu Ende, bevor du beginnst.",
          text: "dfgj kjfh dfgj kjfh dfgj kjfh dkfj jkdf kfdj dfjk dkfj fdjk",
        },
        {
          key: "s-34rjros6",
          title: "Genau hinsehen",
          hint: "Hier sieht vieles ähnlich aus. Lieber langsam und richtig als schnell und falsch.",
          text: "fjkh dghj khjf fghd gjkh dfgk fjkd fddj dkhj fkkd ghdk dfhg",
        },
        {
          key: "s-z5uflh78",
          title: "Wie weit kommst du?",
          hint: "Im Heft ist das die Minute mit dem roten und dem grünen Strich. Hier zählt Genauigkeit.",
          text: "fjfj dkdk fjfj dkdk dfjk dfjk dfjk dfjk fgjh fgjh fgjh fgjh fjkd dkgj fgkh dkfj fkkh dhdj jgdh khkd",
        },
      ],
    },

    // ── 4e ────────────────────────────────────────────────────────── S. 16–17
    {
      key: "l-drs6p9mb",
      number: 5,
      order: 4,
      title: "Ringfinger S und L",
      subtitle: "Heft 4e · Die Ringfinger sind dran",
      color: "#4E9AF1",
      keys: ["s", "d", "f", "g", "h", "j", "k", "l", " "],
      intro:
        "Der linke Ringfinger tippt s, der rechte Ringfinger tippt l. Ringfinger sind ungeübt und wollen gern mitschwimmen – gib ihnen Zeit.",
      stages: [
        {
          key: "s-ljrruaah",
          title: "s und l einzeln",
          hint: "Viermal derselbe Buchstabe. Der Finger bleibt auf seiner Taste stehen.",
          text: "ssss ssss ssss ssss llll llll llll llll",
        },
        {
          key: "s-yipju3jq",
          title: "s und l im Wechsel",
          hint: "Links, rechts, links, rechts – spüre den Rhythmus.",
          text: "ll ss ll ss ll ss ll ss ll ss ll ssll ssll ssll llss llss llss",
        },
        {
          key: "s-lxnpqodn",
          title: "Verdreht",
          hint: "Jetzt stehen s und l bunt gemischt. Zeichen für Zeichen lesen.",
          text: "slsl slsl slsl slsl lsls lsls lsls lssl slsl ssls lsls slls lssl",
        },
        {
          key: "s-8e1ak6yq",
          title: "Mit den anderen Fingern",
          hint: "sdf und jkl sind halbe Grundstellungen – die kannst du bald blind.",
          text: "sdf sdf sdf sdf jkl jkl jkl jkl slf slf slf sfl hlk hlk hlk slsl ss fkl sdk ksl dls lsk dkl",
        },
        {
          key: "s-dko5hn95",
          title: "Die ganze Reihe entlang",
          hint: "Acht Tasten am Stück: s – d – f – g – h – j – k – l. Von links nach rechts durch.",
          text: "sdfg sdfg sdfg dfgh dfgh dfgh fghj fghj fghj ghjk ghjk ghjk hjkl hjkl hjkl",
        },
      ],
    },

    // ── 4f ───────────────────────────────────────────────────────────── S. 18
    {
      key: "l-4l0niilx",
      number: 6,
      order: 5,
      title: "Kleiner Finger A und Ö",
      subtitle: "Heft 4f · Die kleinen Finger sind dran",
      color: "#9B7EDE",
      keys: ["a", "s", "d", "f", "g", "h", "j", "k", "l", "ö", " "],
      intro:
        "Der linke kleine Finger tippt a, der rechte kleine Finger tippt ö. Die kleinen Finger sind die schwächsten von allen – sie brauchen am meisten Übung.",
      stages: [
        {
          key: "s-f2vkw736",
          title: "a und ö einzeln",
          hint: "Nur der kleine Finger arbeitet, die Hand bleibt ruhig liegen.",
          text: "aaaa aaaa aaaa aaaa öööö öööö öööö öööö",
        },
        {
          key: "s-2do8qksr",
          title: "a und ö im Wechsel",
          hint: "Ganz außen links, ganz außen rechts – der weiteste Weg auf der Grundreihe.",
          text: "aö aö aö aö öa öa öa öa öa öa aaö aaö aaö aaö ööa ööa ööa ööa",
        },
        {
          key: "s-7v3az370",
          title: "Die ganze Grundstellung",
          hint: "asdf und jklö – das ist genau die Grundstellung, einmal von außen nach innen.",
          text: "asdf asdf asdf asdf asdf asdf jklö jklö jklö jklö jklö jklö",
        },
        {
          key: "s-1i56vd72",
          title: "Alles gemischt",
          hint: "Zehn Tasten, zehn Finger. Nach jeder Gruppe kurz in die Grundstellung zurück.",
          text: "aslö aslö aslö aslö aslö fjlö fjlö fjlö gklö gklö gklö jkas jkas jkas jkas jkas",
        },
      ],
    },

    // ── 4g ───────────────────────────────────────────────────────────── S. 19
    {
      key: "l-8a2qw73r",
      number: 7,
      order: 6,
      title: "Kleiner Finger Ä",
      subtitle: "Heft 4g · Bewegung nach rechts, und die ersten Wörter",
      color: "#9B7EDE",
      keys: ["a", "s", "d", "f", "g", "h", "j", "k", "l", "ö", "ä", " "],
      intro:
        "Der rechte kleine Finger geht noch ein Stück weiter nach rechts auf das ä. Mit a, ö und ä kannst du jetzt die ersten richtigen Wörter schreiben.",
      stages: [
        {
          key: "s-qdw7s93f",
          title: "ä üben",
          hint: "Vom ö einen Schritt nach rechts – und sofort zurück.",
          text: "aa ää aa ää aa ää aa ää aäa aäa aäa aäa aäa aäa",
        },
        {
          key: "s-78x8mz0p",
          title: "Noch einmal a und ö",
          hint: "Damit der kleine Finger nicht durcheinanderkommt: ö und ä sind Nachbarn.",
          text: "aö aö aö aö öa öa öa öa öa öa aaö aaö aaö aaö ööa ööa ööa ööa",
        },
        {
          key: "s-iwf914v7",
          title: "Die ersten Wörter",
          hint: "ja und das sind echte Wörter – und du kannst sie schon tippen!",
          text: "ja ja ja ja ja ja ja ja ja ja ja das das das das das das das",
        },
        {
          key: "s-sjq9n3z0",
          title: "Noch mehr Wörter",
          hint: "Bei hals arbeiten vier verschiedene Finger nacheinander.",
          text: "da da da da da da da da da da hals hals hals hals hals hals hals",
        },
      ],
    },

    // ── 4h ────────────────────────────────────────────────────────── S. 20–22
    {
      key: "l-lr95k1sj",
      number: 8,
      order: 7,
      title: "Grundreihe üben",
      subtitle: "Heft 4h · Wörter und Sätze aus der Grundreihe",
      color: "#8CA3B5",
      keys: ["a", "s", "d", "f", "g", "h", "j", "k", "l", "ö", "ä", " "],
      intro:
        "Die Grundreihe ist geschafft! Jetzt beginnt das Training. Je mehr du übst, desto leichter wird das Tippen – und am Ende stehen richtige Sätze.",
      stages: [
        {
          key: "s-185m9935",
          title: "Wörter richtig schreiben",
          hint: "Im Heft musst du hier das falsche Wort einkreisen. Tippe die richtigen ab.",
          text: "glas glas glas glas jagd jagd jagd jagd fass fass fass fass fall fall fall fall",
        },
        {
          key: "s-sdy53fst",
          title: "Kurze Wörter",
          hint: "Alles kleine, häufige Wörter. Der Daumen macht die Lücke dazwischen.",
          text: "all fall falls all fall falls da das las da das las öl öd öl öd alf half alf half",
        },
        {
          key: "s-ywd7yzfl",
          title: "Längere Wörter",
          hint: "Doppelbuchstaben tippt derselbe Finger zweimal hintereinander.",
          text: "das lös das lös das lös das lös skala skala skala skala alaska alaska alaska alaska",
        },
        {
          key: "s-azjkdvp4",
          title: "Wörter zum Staunen",
          hint: "kajak liest sich von vorn und von hinten gleich. Schaffst du es fehlerfrei?",
          text: "jaffa jaffa jaffa kassa kassa kassa saal kalk saal kalk saal kalk kajak kajak kajak",
        },
        {
          key: "s-uxk3z4yt",
          title: "Erste Sätze",
          hint: "Punkte gibt es noch nicht – die Satzzeichen kommen später im Heft.",
          text: "alf sah glas alf las da lag das glas alf half das glas lag da",
        },
        {
          key: "s-gqquce2i",
          title: "Deine Meisterprüfung",
          hint: "Alles, was du in Kapitel 4 gelernt hast, in einer Übung. Ruhig bleiben.",
          text: "alf sah das jagdglas das jagdglas lag da alf sah das kajak lag da das glas ja da lag alaska",
        },
      ],
    },

    // ── 5a ────────────────────────────────────────────────────────── S. 24–25
    {
      key: "l-1n5hawdy",
      number: 9,
      order: 8,
      title: "Umschalttaste",
      subtitle: "Heft 5a · Die kleinen Finger halten fest",
      color: "#9B7EDE",
      // 5a übt die Umschalttaste. Ein Großbuchstabe ohne sie kann nur aus der
      // gerasteten Feststelltaste kommen – und dann scheitert das Kind sowieso
      // am "a" von "Aa", nur ohne zu erfahren, woran.
      uppercaseWith: "shift",
      keys: [
        "a",
        "s",
        "d",
        "f",
        "g",
        "h",
        "j",
        "k",
        "l",
        "ö",
        "ä",
        " ",
        "A",
        "S",
        "D",
        "F",
        "G",
        "H",
        "J",
        "K",
        "L",
        "Ö",
        "Ä",
      ],
      intro:
        "Mit der Umschalttaste schreibst du Großbuchstaben. Drücke sie mit dem kleinen Finger und halte sie fest, tippe gleichzeitig den Buchstaben und lass sie wieder los. Merke: liegt der Buchstabe links, hältst du rechts – und umgekehrt.",
      stages: [
        {
          key: "s-upfdpvlk",
          title: "Halten und tippen",
          hint: "Für A und F hältst du rechts fest, für J und L links. Immer die andere Hand.",
          text: "A A A A A F F F F F JJJJJ LLLLL",
        },
        {
          key: "s-91mz381o",
          title: "Groß und klein im Wechsel",
          hint: "Loslassen nicht vergessen – sonst wird auch der zweite Buchstabe groß.",
          text: "Aa Aa Aa Aa Aa Aa Hh Hh Hh Hh Hh Hh Sl Sl Sl Sl Sl Sl Sl Ja Ja Ja Ja Ja Ja Ja",
        },
        {
          key: "s-ap5lva9n",
          title: "Der Große in der Mitte",
          hint: "Klein, groß, klein: die Umschalttaste kommt nur für einen Anschlag dazu.",
          text: "gGg gGg gGg kKk kKk kKk dDä dDä dDä Ssj Ssj Ssj",
        },
        {
          key: "s-d5jcvxi4",
          title: "Wörter mit großem Anfang",
          hint: "Nur der erste Buchstabe ist groß. Danach liegen die Finger wieder normal.",
          text: "Hals Hals Hals Hals Hals Hals Kajak Kajak Kajak Kajak Kajak",
        },
        {
          key: "s-hxww02fh",
          title: "Noch mehr Wörter",
          hint: "Bei Jagd und Saal hältst du beide Male rechts fest.",
          text: "Jagd Jagd Jagd Jagd Jagd Jagd Saal Saal Saal Saal Saal Saal",
        },
        {
          key: "s-oi6hz68r",
          title: "Alles gemischt",
          hint: "Mal links, mal rechts halten. Vor jedem Wort kurz überlegen.",
          text: "Fall Skala Hass Öl Alaska Gas Jagd Salsa Kalk Kajak Aal Fass",
        },
        {
          key: "s-4fekprfq",
          title: "Sätze mit großem Anfang",
          hint: "Jetzt siehst du, wofür die Umschalttaste da ist: jeder Satz fängt groß an.",
          text: "Alf sah Glas Da lag das Glas Alf sah das Jagdglas Ja da lag Alaska",
        },
      ],
    },

    // ── 5b ───────────────────────────────────────────────────────────── S. 26
    {
      key: "l-vviszvh3",
      number: 10,
      order: 9,
      title: "Feststelltaste",
      subtitle: "Heft 5b · Wenn alles groß bleiben soll",
      color: "#4E9AF1",
      // Das ist die Lektion. Ohne diese Zeile ließe sie sich vollständig mit
      // gehaltener Umschalttaste durchtippen – und hieße dann zu Unrecht so.
      uppercaseWith: "capsLock",
      keys: [
        "a",
        "s",
        "d",
        "f",
        "g",
        "h",
        "j",
        "k",
        "l",
        "ö",
        "ä",
        " ",
        "A",
        "S",
        "D",
        "F",
        "G",
        "H",
        "J",
        "K",
        "L",
        "Ö",
        "Ä",
      ],
      intro:
        "Für viele Großbuchstaben hintereinander gibt es die Feststelltaste. Drücke sie einmal mit dem linken kleinen Finger – ab jetzt schreibt alles groß. Noch einmal drücken, und es ist wieder klein.",
      stages: [
        {
          key: "s-xb98b098",
          title: "Die Grundstellung in groß",
          hint: "Einmal drücken, dann tippen wie immer. Die Umschalttaste brauchst du nicht.",
          text: "ASDF ASDF ASDF ASDF JKLÖ JKLÖ JKLÖ JKLÖ",
        },
        {
          key: "s-pz3gh67r",
          title: "Ein Wort in groß",
          hint: "Die Feststelltaste bleibt gedrückt – du musst nichts festhalten.",
          text: "JA JA JA JA JA JA JA JA",
        },
        {
          key: "s-juplvi5q",
          title: "Wörter in groß",
          hint: "Am Ende der Übung noch einmal drücken, sonst bleibt alles groß.",
          text: "GLAS KALK SKALA ALL JAGD",
        },
      ],
    },
  ],
};
